/**
 * Format アプリ専用カラーテーマ
 *
 * このファイルは、Formatアプリケーションで使用される
 * コンポーネントごとのカラー設定を定義します。
 *
 * 使用方法:
 * import { formatTheme } from './theme/formatTheme';
 *
 * // コンポーネントの色を取得
 * const buttonColors = formatTheme.components.button;
 */

import { colors } from "@ippon/shared-components";

// コンポーネントごとのカラー設定
export const formatTheme = {
  // デフォルトのカラーパレット（shared-componentsから継承）
  colors: colors,
  // colors: {
  //   ...colors,
  //   border: {
  //     ...colors.border,
  //     // Format アプリでは CustomButton などの枠線を黄色系に変更
  //     medium: "#FFEB3B",
  //     dark: "#FBC02D",
  //   },
  // },

  // コンポーネントごとのカラー設定
  components: {
    // ボタンコンポーネント
    button: {
      // プライマリボタン
      primary: {
        background: colors.primary.main,
        color: colors.text.inverse,
        border: colors.primary.main,
        hover: {
          background: colors.primary.hover,
          color: colors.text.inverse,
          border: colors.primary.hover,
        },
        active: {
          background: colors.primary.active,
          color: colors.text.inverse,
          border: colors.primary.active,
        },
        disabled: {
          background: colors.text.secondary,
          color: colors.text.inverse,
          border: colors.text.secondary,
        },
      },
      // セカンダリボタン（クリアボタンなど）
      secondary: {
        background: colors.background.default,
        color: colors.text.secondary,
        border: colors.border.medium,
        hover: {
          background: colors.background.hover,
          color: colors.text.secondary,
          border: colors.border.dark,
        },
        active: {
          background: colors.background.hover,
          color: colors.text.secondary,
          border: colors.border.dark,
        },
        disabled: {
          background: colors.background.disabled,
          color: colors.text.disabled,
          border: colors.border.light,
        },
      },
      // エラーボタン
      error: {
        background: colors.status.error,
        color: colors.text.inverse,
        border: colors.status.error,
        hover: {
          background: "#C62828", // エラーのダークバージョン
          color: colors.text.inverse,
          border: "#C62828",
        },
        active: {
          background: "#B71C1C", // エラーのよりダークバージョン
          color: colors.text.inverse,
          border: "#B71C1C",
        },
        disabled: {
          background: colors.text.secondary,
          color: colors.text.inverse,
          border: colors.text.secondary,
        },
      },
    },

    // テキスト入力コンポーネント
    textInput: {
      // 通常の入力フィールド
      default: {
        background: colors.background.default,
        color: colors.text.primary,
        border: colors.border.medium,
        placeholder: colors.text.placeholder,
        focus: {
          background: colors.background.default,
          color: colors.text.primary,
          border: colors.border.focus,
        },
        hover: {
          background: colors.background.default,
          color: colors.text.primary,
          border: colors.border.medium,
        },
        disabled: {
          background: colors.background.disabled,
          color: colors.text.primary,
          border: colors.border.medium,
        },
        error: {
          background: colors.background.default,
          color: colors.text.primary,
          border: colors.border.error,
        },
      },
      // ラベル
      label: {
        color: colors.text.primary,
        required: colors.status.error,
      },
      // 単位表示（円、㎡など）
      unit: {
        color: colors.text.primary,
      },
    },

    // セレクトコンポーネント
    select: {
      // 通常のセレクト
      default: {
        background: colors.background.default,
        color: colors.text.primary,
        border: colors.border.medium,
        placeholder: colors.text.placeholder,
        focus: {
          background: colors.background.default,
          color: colors.text.primary,
          border: colors.border.focus,
        },
        hover: {
          background: colors.background.default,
          color: colors.text.primary,
          border: colors.border.medium,
        },
        disabled: {
          background: colors.background.disabled,
          color: colors.text.primary,
          border: colors.border.medium,
        },
        error: {
          background: colors.background.default,
          color: colors.text.primary,
          border: colors.border.error,
        },
      },
      // ドロップダウンメニュー
      menu: {
        background: colors.background.default,
        color: colors.text.primary,
        hover: colors.background.hover,
        selected: colors.background.selected,
      },
      // チェックボックス
      checkbox: {
        checked: colors.primary.main,
        unchecked: colors.border.medium,
        disabled: colors.text.disabled,
      },
    },

    // モーダルコンポーネント
    modal: {
      // モーダル背景
      background: colors.background.default,
      // オーバーレイ
      overlay: colors.background.overlay,
      // シャドウ
      shadow: colors.shadow.modal,
      // タイトル
      title: {
        color: colors.text.primary,
      },
      // コンテンツ
      content: {
        color: colors.text.primary,
      },
      // ボタンエリア
      actions: {
        background: colors.background.default,
        border: colors.border.light,
      },
    },

    // テーブルコンポーネント
    table: {
      // ヘッダー
      header: {
        background: colors.background.light,
        color: colors.text.primary,
        border: colors.border.light,
      },
      // 行
      row: {
        background: colors.background.default,
        color: colors.text.primary,
        border: colors.border.light,
        hover: colors.background.hover,
        selected: colors.background.selected,
      },
      // セル
      cell: {
        color: colors.text.primary,
        border: colors.border.light,
      },
      // ページネーション
      pagination: {
        background: colors.background.default,
        color: colors.text.primary,
        border: colors.border.light,
      },
    },

    // ファイルアップロードコンポーネント
    fileUpload: {
      // アップロードエリア
      area: {
        background: colors.background.lighter,
        border: colors.primary.main,
        color: colors.text.primary,
        hover: {
          background: colors.primary.light,
          border: colors.primary.main,
        },
      },
      // ファイルリスト
      fileList: {
        background: colors.background.light,
        color: colors.text.primary,
        border: colors.border.light,
      },
      // 削除ボタン
      deleteButton: {
        background: colors.background.default,
        color: colors.text.secondary,
        border: colors.border.medium,
        hover: {
          background: colors.status.error,
          color: colors.text.inverse,
        },
      },
    },

    // セクションタイトルコンポーネント
    sectionTitle: {
      color: colors.text.primary,
      border: colors.border.light,
    },

    // ローディングコンポーネント
    loading: {
      color: colors.primary.main,
      background: colors.background.overlay,
    },
  },

  // レイアウト関連の色
  layout: {
    // ヘッダー
    header: {
      background: colors.background.default,
      color: colors.text.primary,
      border: colors.border.light,
    },
    // サイドバー
    sidebar: {
      background: colors.background.light,
      color: colors.text.primary,
      border: colors.border.light,
      active: colors.primary.main,
      hover: colors.background.hover,
    },
    // メインコンテンツ
    main: {
      background: colors.background.default,
      color: colors.text.primary,
    },
    // フッター
    footer: {
      background: colors.background.light,
      color: colors.text.secondary,
      border: colors.border.light,
    },
  },

  // 状態別の色
  states: {
    // 成功状態
    success: {
      background: "#E8F5E8",
      color: colors.status.success,
      border: colors.status.success,
    },
    // 警告状態
    warning: {
      background: "#FFF3E0",
      color: colors.status.warning,
      border: colors.status.warning,
    },
    // エラー状態
    error: {
      background: "#FFEBEE",
      color: colors.status.error,
      border: colors.status.error,
    },
    // 情報状態
    info: {
      background: "#E3F2FD",
      color: colors.status.info,
      border: colors.status.info,
    },
  },
} as const;

// 型定義
export type FormatTheme = typeof formatTheme;
export type ComponentColors = typeof formatTheme.components;
export type ButtonColors = typeof formatTheme.components.button;
export type TextInputColors = typeof formatTheme.components.textInput;
export type SelectColors = typeof formatTheme.components.select;
export type ModalColors = typeof formatTheme.components.modal;
export type TableColors = typeof formatTheme.components.table;
export type FileUploadColors = typeof formatTheme.components.fileUpload;
export type LayoutColors = typeof formatTheme.layout;
export type StateColors = typeof formatTheme.states;

// カラー取得用のヘルパー関数
export const getComponentColors = <K extends keyof ComponentColors>(
  component: K
): ComponentColors[K] => {
  return formatTheme.components[component];
};

export const getButtonColors = <K extends keyof ButtonColors>(
  variant: K
): ButtonColors[K] => {
  return formatTheme.components.button[variant];
};

export const getTextInputColors = <K extends keyof TextInputColors>(
  state: K
): TextInputColors[K] => {
  return formatTheme.components.textInput[state];
};

export const getSelectColors = <K extends keyof SelectColors>(
  state: K
): SelectColors[K] => {
  return formatTheme.components.select[state];
};

export const getLayoutColors = <K extends keyof LayoutColors>(
  section: K
): LayoutColors[K] => {
  return formatTheme.layout[section];
};

export const getStateColors = <K extends keyof StateColors>(
  state: K
): StateColors[K] => {
  return formatTheme.states[state];
};
